// Get a client-side crash into the SERVER log, from a browser that is about to
// be closed.
//
// ⚠️ THE USER IS NOT THE TRANSPORT. The realistic behaviour when something
// breaks is a phone photo of the screen and then closing the tab — nobody is
// going to select a stack trace and paste it into Slack. So the report has to
// leave the browser by itself, in the seconds before the window goes away, and
// the only thing on screen the person needs to capture is a short REFERENCE.
//
// sendBeacon IS THE WHOLE REASON THIS WORKS. It hands the payload to the
// browser, which owns delivery from then on — it is explicitly allowed to
// outlive the document, it does not block unload, and it cannot be cancelled by
// the navigation that is already happening. A plain fetch() is aborted when the
// page goes; `keepalive: true` is the standard's answer to that and is the
// fallback here, but sendBeacon is the one with the strongest guarantee, so it
// is tried first.
//
// It is fire-and-forget in the strict sense: nothing here awaits a response,
// nothing retries, and every failure path is swallowed. A reporter that can
// throw turns one broken page into two, and the second one is inside the error
// handler where nothing is left to catch it.

/** What the browser sends. `ref` is the half the human can read off a photo. */
export interface ClientErrorReport {
  ref: string;
  message: string;
  stack?: string;
  /** React's hash for a server-render error — the half that survives minification. */
  digest?: string;
  /**
   * render = an error boundary; window/unhandledrejection = the global
   * listeners; write-refused = a mutation the server would not take;
   * action-refused = a button the UI drew that the stage model then declined.
   *
   * THE LAST TWO ARE NOT CRASHES, and they are here anyway. Nothing threw — the
   * app is still running and still looks fine, which is precisely what makes them
   * worth a log line more than most exceptions are. They travel the same pipe
   * because the pipe is the part that works: a beacon that outlives the document,
   * a ref the user can photograph, and the identity stamped on the server where
   * it cannot be forged.
   *
   * `action-refused` IS ALWAYS A UI BUG, even though it is never a user-visible
   * crash: the buttons are drawn from the same stage model that then refused the
   * click, so the two disagreeing means one of them is reading state the other
   * cannot see. Usually a stale tab. Sometimes not, and those are the ones worth
   * finding in a log.
   *
   * `notify-failed` IS THE ONE THAT IS DELIBERATELY NOT SHOWN TO ANYBODY. A Slack
   * message that did not send is a background errand, not the user's business —
   * their approval landed, the app has it, and interrupting a client to explain
   * that somebody's account manager was not DMed is telling them about an
   * internal workflow they are not in. It still has to reach US, because the AM
   * on the other end is waiting for a message nobody knows did not arrive.
   */
  source:
    | "render"
    | "global-render"
    | "window"
    | "unhandledrejection"
    | "write-refused"
    | "action-refused"
    | "notify-failed";
  url: string;
  userAgent: string;
  /**
   * Whatever the caller knows that the message does not — the row, the status,
   * the operation. Flat, small, and never anything the user typed.
   *
   * ⚠️ NEVER THE PAYLOAD. It is tempting to attach the value that failed to
   * save, because it would make the log complete. It would also copy a script's
   * text, a comment, or whatever else a person wrote into a log line that lives
   * on disk for months. The kind and the id are enough to find the row; the row
   * is where the value already is.
   */
  context?: Record<string, string | number | boolean>;
}

export const CLIENT_ERROR_ENDPOINT = "/api/client-error";

// No 0/O/1/I/L. The entire point is that somebody reads this off a photograph of
// a screen, possibly at an angle, possibly on a cracked phone, and types it into
// Slack — so the alphabet cannot contain a pair that argue about which one it is.
const ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

/** A short, unambiguous, photographable id: "K7F-3QM". */
export function makeRef(random: () => number = Math.random): string {
  const pick = () => ALPHABET[Math.floor(random() * ALPHABET.length)];
  const run = (n: number) => Array.from({ length: n }, pick).join("");
  return `${run(3)}-${run(3)}`;
}

/** Stacks can be enormous, and a log line is not a place to find out how big. */
const CAP = 4000;
const clip = (s: string | undefined, n: number): string | undefined =>
  s === undefined ? undefined : s.length > n ? `${s.slice(0, n)}…[clipped]` : s;

// ONE PAGE LOAD, A FEW REPORTS. A render loop can throw the same error hundreds
// of times a second, and a reporter with no ceiling turns that into hundreds of
// beacons and hundreds of log lines about one bug. The seen-set collapses the
// repeats; the counter is the backstop for errors that differ every time (a
// message with a timestamp in it, say).
const MAX_PER_PAGE = 5;
let sent = 0;
const seen = new Set<string>();

/** Tests only. */
export function resetReportLimits(): void {
  sent = 0;
  seen.clear();
}

/**
 * Post a crash to the server. Returns the ref it filed under, or null if it
 * declined to send (a repeat, or over the per-page ceiling).
 *
 * NOTHING HERE MAY THROW. Every caller is already handling an error.
 */
export function reportClientError(
  input: {
    error: unknown;
    source: ClientErrorReport["source"];
    digest?: string;
    ref?: string;
    context?: ClientErrorReport["context"];
  },
): string | null {
  try {
    if (sent >= MAX_PER_PAGE) return null;

    const err = input.error;
    const message =
      err instanceof Error ? err.message
        : typeof err === "string" ? err
        : (() => { try { return JSON.stringify(err); } catch { return String(err); } })();
    const stack = err instanceof Error ? err.stack : undefined;

    // Keyed on what the bug IS, not on where it was caught — the same throw
    // arriving via the boundary and via window.onerror is one bug.
    const key = `${message}|${stack ?? ""}`;
    if (seen.has(key)) return null;
    seen.add(key);
    sent++;

    const ref = input.ref ?? makeRef();
    const report: ClientErrorReport = {
      ref,
      message: clip(message, 1000) ?? "(no message)",
      stack: clip(stack, CAP),
      digest: input.digest,
      source: input.source,
      url: typeof location === "undefined" ? "" : `${location.pathname}${location.search}`,
      userAgent: typeof navigator === "undefined" ? "" : navigator.userAgent,
      context: input.context,
    };

    const body = JSON.stringify(report);

    // A Blob, so the request carries a real Content-Type. sendBeacon with a bare
    // string sends text/plain, which a JSON route has to sniff around.
    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      const ok = navigator.sendBeacon(CLIENT_ERROR_ENDPOINT, new Blob([body], { type: "application/json" }));
      if (ok) return ref;
      // sendBeacon returns false when the payload is over the browser's queue
      // budget. Fall through rather than lose the report.
    }

    if (typeof fetch === "function") {
      void fetch(CLIENT_ERROR_ENDPOINT, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body,
        keepalive: true,
        cache: "no-store",
      }).catch(() => {});
    }

    return ref;
  } catch {
    return null; // a reporter that throws makes one broken page into two
  }
}
